import { useState } from "react";
import moment from "jalali-moment";
import { Modal } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ScoreModal from "../modal/ScoreModal";

/**
 * A closed assignment: the score, then what you can still download.
 *
 * The three download buttons were previously disabled by pointer-events with no
 * visual difference, so an unavailable file looked identical to an available
 * one. Missing files now simply do not render a button.
 *
 * Removed with this rewrite: a trailing `const exams = []` left over from an
 * earlier version.
 */

function FileLink({ href, icon: Icon, label, emphasis }) {
    if (!href || href === "null" || href === "undefined") return null;
    return (
        <a
            href={href}
            download
            className={`inline-flex items-center gap-xs rounded-soft border px-lg py-xs text-sm transition-colors duration-150 hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none ${
                emphasis
                    ? "border-primary text-primary"
                    : "border-rule text-ink-muted hover:text-ink"
            }`}
        >
            <Icon sx={{ fontSize: "1.8rem" }} />
            {label}
        </a>
    );
}

const PreviousHW = ({ assignmentInfo }) => {
    const [scoreModal, setScoreModal] = useState(false);
    const [score, setScore] = useState();

    const handleClose = () => setScoreModal(false);
    const handleModal = (data) => {
        setScoreModal(true);
        setScore(data);
    };

    return (
        <>
            <article className="flex flex-col gap-lg rounded-panel border border-rule-subtle bg-surface p-xl shadow-card">
                <div className="flex flex-wrap items-start justify-between gap-md">
                    <div className="min-w-0">
                        <h3 className="text-base font-semibold text-ink">
                            {assignmentInfo.AssignmentName}
                        </h3>
                        {assignmentInfo.assignment_headline && (
                            <p className="mt-2xs text-sm text-ink-subtle">
                                {assignmentInfo.assignment_headline}
                            </p>
                        )}
                        <p className="mt-2xs text-sm tabular-nums text-ink-subtle">
                            {moment(
                                assignmentInfo.assignment_available_time_end,
                                "YYYY/MM/DD"
                            )
                                .locale("fa")
                                .format("DD MMMM YYYY")}
                        </p>
                    </div>

                    <div className="flex items-baseline gap-2xs">
                        <span className="text-sm text-ink-subtle">نمره</span>
                        <span className="text-2xl font-semibold tabular-nums text-primary">
                            {Number(
                                assignmentInfo.score?.score ?? 0
                            ).toLocaleString("fa-IR")}
                        </span>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-sm border-t border-rule-subtle pt-lg">
                    <FileLink
                        href={assignmentInfo.assignment_file}
                        icon={DescriptionIcon}
                        label="صورت تکلیف"
                    />
                    <FileLink
                        href={assignmentInfo.assignment_student_file}
                        icon={DownloadIcon}
                        label="پاسخ من"
                    />
                    <FileLink
                        href={assignmentInfo.score?.assignment_teacher_file}
                        icon={FactCheckIcon}
                        label="برگه تصحیح‌شده"
                        emphasis
                    />
                    <button
                        type="button"
                        onClick={() => handleModal(assignmentInfo.score)}
                        className="inline-flex items-center rounded-soft border border-rule px-lg py-xs text-sm text-ink-muted transition-colors duration-150 hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none"
                    >
                        ریز نمرات
                    </button>
                </div>
            </article>

            <Modal
                open={Boolean(scoreModal)}
                onClose={handleClose}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <ScoreModal close={handleClose} scores={score} />
            </Modal>
        </>
    );
};

export default PreviousHW;
